<template>
  <v-card elevation="0">
    <span
      data-testid="home-text-challengeTitle"
      class="caption blue-grey--text text--darken-1 font-weight-medium"
    >
      {{ $t('home.activity_board.challenges.challenges_title') }}
    </span>

    <v-card class="card-border my-3" outlined>
      <v-row v-if="isLoading" class="py-2">
        <v-col v-for="item in 3" cols="12" md="12" :key="`skeleton-${item}`">
          <ChallengeItemSkeleton />
        </v-col>
      </v-row>

      <div
        v-if="!hasActiveChallenges"
        class="d-flex flex-column align-start px-3 py-3"
      >
        <div class="d-flex align-center">
          <v-avatar size="32" color="blue">
            <v-icon color="white">mdi-clipboard-text-outline </v-icon>
          </v-avatar>

          <div class="ms-2">
            <p class="subtitle2 blue-grey--text text--darken-2 mb-0">
              {{ $t('home.activity_board.challenges.no_active_challenges') }}
            </p>
          </div>
        </div>

        <div class="d-flex flex-column mt-3 w-100">
          <Button
            v-if="canCreateChallenges()"
            color="primaryLighten"
            data-testid="home-button-createChallenge"
            size="small"
            :text="$t('home.activity_board.challenges.create_challenge_button')"
            @click="openChallengeModal"
          />
        </div>
      </div>

      <v-row v-if="hasActiveChallenges" no-gutters>
        <v-col
          v-for="challenge in activeChallenges"
          cols="12"
          :key="getChallengeId(challenge)"
        >
          <ChallengeItem :challenge="challenge" />
        </v-col>

        <div class="d-flex flex-column px-3 py-4 w-100">
          <Button
            color="primaryLighten"
            data-testid="home-button-viewAllChallenges"
            size="small"
            :text="$t('home.activity_board.challenges.view_all_button')"
            :to="{
              name: 'explore',
              query: {
                types: ['challenge'],
              },
            }"
          />
        </div>
      </v-row>
    </v-card>

    <ChallengeModal :show="showChallengeModal" @close="closeChallengeModal" />
  </v-card>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { IMAGE_SIZES } from 'rever-media-sdk'
import { utils } from 'frontend-js-shared'

import challengePermissions from '@/mixins/challengePermissions.vue'
import mixin from '@/mixins/mixin.vue'
import userMixin from '@/mixins/userMixin.vue'

import ChallengeModal from '@/components/ChallengeModal/ChallengeModal.vue'
import Button from '@/components/UI/Button.vue'

import ChallengeItem from './ChallengeItem.vue'
import ChallengeItemSkeleton from './ItemSkeleton.vue'

import { HOME_WIDGET_FETCH_DELAYS } from '../constants'

export default {
  name: 'Challenges',

  mixins: [challengePermissions, mixin, userMixin],

  components: {
    ChallengeModal,
    Button,
    ChallengeItem,
    ChallengeItemSkeleton,
  },

  data() {
    return {
      IMAGE_SIZES,
      cachedChallenges: [],
      fetchTimer: null,
      isLoading: true,
      isShowingCachedSnapshot: false,
      showChallengeModal: false,
    }
  },

  mounted() {
    this.hydrateFromSnapshot()
    this.fetchTimer = setTimeout(this.getChallenges, HOME_WIDGET_FETCH_DELAYS.activityBoard)
  },

  beforeDestroy() {
    clearTimeout(this.fetchTimer)
  },

  computed: {
    ...mapGetters(['accountId', 'organizationId']),

    activeChallenges() {
      if (this.isShowingCachedSnapshot) return this.cachedChallenges

      return this.$store.state.challenge.challenges
    },

    hasActiveChallenges() {
      return !this.isLoading && !_.isEmpty(this.activeChallenges)
    },
  },

  methods: {
    getChallengeId(challenge) {
      return utils.getEntityId(challenge)
    },

    hydrateFromSnapshot() {
      const snapshot = this.$store.getters['homeSnapshots/validSnapshot'](
        'activityBoard'
      )

      if (!_.isArray(snapshot?.challenges)) return

      this.cachedChallenges = [...snapshot.challenges]
      this.isLoading = false
      this.isShowingCachedSnapshot = true
    },

    saveSnapshot() {
      this.$store.commit('homeSnapshots/SET_ACTIVITY_BOARD_SNAPSHOT', {
        organizationId: this.organizationId,
        accountId: this.accountId,
        challenges: [...this.$store.state.challenge.challenges],
      })
    },

    async getChallenges() {
      try {
        if (!this.isShowingCachedSnapshot) this.isLoading = true

        await this.$store.dispatch('getChallenges', {
          filters: {
            status: 'active',
          },
          organizationId: this.organizationId,
          limit: 3,
          expansions: ['site'],
        })

        this.saveSnapshot()
        this.isShowingCachedSnapshot = false
      } catch (err) {
        this.showError(err.message)
      } finally {
        this.isLoading = false
      }
    },

    openChallengeModal() {
      this.showChallengeModal = true
    },

    closeChallengeModal() {
      this.showChallengeModal = false
    },
  },
}
</script>
